// Generated from docs/product/how-it-works.md by pnpm docs:how-it-works:sync.
// Edit the Markdown source, then regenerate this file.

export const howItWorksContent = {
  "hero": {
    "title": "Your app already works. Give it a way out.",
    "intro": "Turn a project on your computer into a live link anyone you choose can open and use. Drag in the folder and share it in minutes."
  },
  "steps": [
    {
      "number": "01",
      "title": "Make the app where you already work",
      "copy": "Use Claude, Codex, or your usual editor. Doorway publishes finished static output from common website frameworks. Eligible Solo and Team Owners and Creators can also use isolated source builds regardless of email address. The first credential-backed managed runtime additionally requires reviewed custom API and public-safety authority."
    },
    {
      "number": "02",
      "title": "Choose the project folder",
      "copy": "Doorway finds the finished website or a supported source project and shows the selected folder's total size, ignored size, and actual upload size. It automatically leaves out local-only folders such as .git and node_modules. For source projects, it also leaves out generated roots such as .next, out, dist, and build before uploading the source. Doorway warns when any other top-level folder contributes more than 25 MiB and names every file over the 1 MiB per-file limit. Upload limits apply to the remaining files after these exclusions, and the folder is blocked when that upload exceeds 5 MiB or a remaining file exceeds 1 MiB."
    },
    {
      "number": "03",
      "title": "Choose who gets in",
      "copy": "Publish a public link or protect it with a visitor password. Team workspaces can keep apps private to approved coworkers. Opening a private Team app uses one Doorway-branded handoff that reuses the member's signed-in account when available, offers Google sign-in when needed, confirms workspace access, and returns directly to the app."
    },
    {
      "number": "04",
      "title": "Share one link",
      "copy": "Every successful publish becomes an immutable revision. A failed replacement leaves the working version online, and you can take the app offline whenever you need to. Paid plans can connect their own domain after DNS routing and managed SSL are ready."
    }
  ],
  "skills": {
    "title": "Keep your team's best workflows in sync.",
    "intro": "Approve a Claude or Codex skills folder once, then let Doorway Sync share valid skills and publish later content updates in the background. New skills become Team Live automatically for connected Owners and Creators, and every team member stays current through the signed Team feed. A user can mark any local skill Private. Changes that add tools, scripts, network access, dependencies, binaries, or unknown capabilities pause for one review before automatic sharing resumes. Existing published skills establish a local baseline during migration, managed Team copies are never republished as duplicates, and removing a local folder never deletes the Team version. Owners and Creators can also upload one complete skill folder from the Team Skills page in the web app. Both paths apply the same file, secret, capability, 100-file, and 16 MiB checks while Doorway retains validation, attribution, and version history."
  },
  "safety": {
    "intro": "Finished-output publishing runs no project code. It omits local-only files, reports the selected, ignored, and upload byte totals, scans for exposed keys, checks linked assets, and tests the new revision before changing the live link. Large unfamiliar folders receive a separate review warning that explains their contribution to the final upload. The plan-entitled source builder additionally installs locked dependencies with install scripts disabled, removes internet access before framework code runs, and executes one known static builder inside a temporary sandbox.",
    "cards": [
      {
        "status": "Goes online",
        "title": "Finished web files",
        "copy": "HTML, CSS, images, fonts, and browser JavaScript needed by the live app."
      },
      {
        "status": "Stays local",
        "title": "Your private setup",
        "copy": "Environment files, Git history, installed packages, provider keys, and other secret-prone files."
      }
    ]
  },
  "ai": {
    "title": "Bring the AI account you already use.",
    "intro": "Eligible creators can connect an Anthropic, OpenAI, or Google account once, then reuse it across projects. Personal workspaces keep creator ownership, while Team workspaces keep provider credentials under workspace ownership. Each project chooses one compatible model and a monthly AI-run cap. The app calls /.doorway/ai/v1/generate, and Doorway keeps the key and model choice outside the uploaded website.",
    "bullets": [
      "Provider charges stay in your provider account.",
      "Doorway does not meter or resell your AI tokens.",
      "Doorway counts project AI runs and applies technical traffic limits to help protect a key from loops and abuse.",
      "Accepted app files are preserved. Direct provider calls and exposed credentials must be repaired before publishing."
    ]
  },
  "frameworks": {
    "intro": "Doorway accepts finished static output across the full list. Eligible paid workspaces can also use six pinned source-project builders. Five use locked npm or pnpm installs, while Hugo uses a pinned Hugo Extended binary with no dependency-install step.",
    "entries": [
      {
        "name": "Plain HTML, CSS, and JavaScript",
        "output": "index.html",
        "mode": "Publish now"
      },
      {
        "name": "Vite apps",
        "output": "dist",
        "mode": "Publish now; source build available to eligible paid workspaces"
      },
      {
        "name": "Create React App",
        "output": "build",
        "mode": "Publish now; source build available to eligible paid workspaces"
      },
      {
        "name": "Astro static",
        "output": "dist",
        "mode": "Publish now; static source build available to eligible paid workspaces"
      },
      {
        "name": "Next.js static export",
        "output": "out",
        "mode": "Publish now; source build with output: 'export' available to eligible paid workspaces"
      },
      {
        "name": "Next.js managed server",
        "output": "Recognized server recipe and locked npm or pnpm install",
        "mode": "Plan-entitled managed runtime; first adapter supports Profound Global Unsubscribe unchanged"
      },
      {
        "name": "Parcel",
        "output": "dist",
        "mode": "Publish finished output now"
      },
      {
        "name": "SvelteKit static",
        "output": "build",
        "mode": "Publish finished output now with adapter-static"
      },
      {
        "name": "React Router SPA",
        "output": "build/client",
        "mode": "Publish now; source build with ssr: false available to eligible paid workspaces"
      },
      {
        "name": "Nuxt generate",
        "output": ".output/public",
        "mode": "Publish finished output now"
      },
      {
        "name": "Gatsby",
        "output": "public",
        "mode": "Publish finished output now"
      },
      {
        "name": "Hugo",
        "output": "public",
        "mode": "Publish finished output now; full source build available to eligible paid workspaces with local themes or vendored modules"
      },
      {
        "name": "Docusaurus",
        "output": "build",
        "mode": "Publish finished output now"
      },
      {
        "name": "Eleventy",
        "output": "_site",
        "mode": "Publish finished output now"
      }
    ]
  },
  "boundary": {
    "title": "Static output is the default contract",
    "intro": "Every general source build must end in a known folder of static files. Doorway does not accept arbitrary install commands or custom shell commands. The managed-runtime path can start one recognized Next.js server recipe with fixed commands, reviewed egress hosts, Doorway-managed credentials, a health check, and an exact-revision activation gate."
  },
  "roadmap": {
    "title": "A practical path to more projects",
    "intro": "Doorway can widen static-build compatibility in controlled steps without turning the first product into a general-purpose cloud runtime.",
    "items": [
      {
        "capability": "Custom outbound API actions",
        "direction": "Available to eligible paid workspaces",
        "requiredBoundary": "Secret-free versioned recipes, server-held credentials, fixed HTTPS destinations, protected or safety-admitted app access, bounded requests and responses, DNS and redirect checks, idempotency, auditability, and exact-revision activation"
      },
      {
        "capability": "Unchanged full-stack apps",
        "direction": "Plan-entitled managed runtime implemented for one Next.js recipe",
        "requiredBoundary": "Locked install, fixed build and start commands, isolated process, capability-based credential injection, reviewed host interception, health checks, protected ingress, exact-revision activation, and resource and cost caps"
      },
      {
        "capability": "Hugo source projects",
        "direction": "Plan-entitled static adapter implemented",
        "requiredBoundary": "Pinned Hugo Extended, clean deterministic public/ output, no build network, local themes or vendored modules, escaping-mount rejection, and real-container CI proof"
      },
      {
        "capability": "Yarn and Bun",
        "direction": "Add after npm and pnpm pressure tests",
        "requiredBoundary": "Pin the package-manager version, require an immutable lockfile, disable install scripts, and limit dependency downloads to approved registries"
      },
      {
        "capability": "Custom build scripts",
        "direction": "Later advanced static-build mode",
        "requiredBoundary": "Let the creator select one declared build script, then run it with no secrets, no network, strict time and resource limits, and the same output validation"
      },
      {
        "capability": "Monorepos",
        "direction": "Later explicit project targeting",
        "requiredBoundary": "Select one workspace and one output contract, install from the repository root, and prevent reads or output outside that workspace boundary"
      },
      {
        "capability": "General server runtimes, jobs, and WebSockets",
        "direction": "Separate runtime product",
        "requiredBoundary": "Expand adapter coverage, process supervision, abuse controls, durable state boundaries, retention, observability, recovery, and per-project cost caps before outside enrollment"
      }
    ]
  }
} as const;
